import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useBusiness } from '@/contexts/BusinessContext';
import { useMonth } from '@/contexts/MonthContext';
import { getPayments, getExpenses, getDailyRate } from '@/lib/api';
import { getMonthRange, formatCurrency, getMonthName, getMonthTimestamps, formatDate } from '@/lib/dateUtils';
import { fetchStripeDashboardMetrics, fetchStripeDailyBalances, fetchStripePayouts, fetchStripeRefunds, fetchStripeTransactions } from '@/lib/stripeService';
import type { StripeDailyBalance, StripePayout, StripeRefund, StripeTransaction } from '@/lib/stripeService';
import { decryptStripeKey, decryptWiseKey, decryptMercuryKey } from '@/lib/encryption';
import { generatePDFReport } from '@/lib/pdfReport';
import { supabase } from '@/lib/supabase';
import LogoSpinner from '@/components/LogoSpinner';
import { calcExchangeRevenue, calcBankCostSplit, calcExchangeProfitSplit, calcNetProfit } from '@/lib/exchangeCalc';
import { fetchWiseProfiles, fetchWiseBalances, type WiseBalance } from '@/lib/wiseService';
import { fetchMercuryAccounts, fetchMercuryTransactions, type MercuryAccount, type MercuryTransaction } from '@/lib/mercuryService';
import type { PaymentWithDetails, Expense } from '@/types/database';
import BarChart from '@/components/charts/BarChart';
import DonutChart from '@/components/charts/DonutChart';
import WaterfallChart from '@/components/charts/WaterfallChart';
import MiniLineChart from '@/components/charts/MiniLineChart';
import AccountsReportSection from '@/pages/reports/AccountsReportSection';
import {
  RevenueVsFeesSection,
  DailyRevenueStackedSection,
  TransactionHeatmapSection,
  PerformanceGaugesSection,
  PayoutTimelineSection,
  RefundSummarySection,
  TransactionTypeSection,
  CumulativeRevenueSection,
} from '@/pages/reports/StripeAnalyticsSections';
import { ChartBar as BarChart3, DollarSign, Receipt, TrendingUp, TrendingDown, ChevronLeft, ChevronRight, Wallet, FileText, Users, Calendar, ArrowRight, Info } from 'lucide-react';

export default function ReportsPage() {
  const { selectedBusiness } = useBusiness();
  const { selectedMonth, setSelectedMonth, isAllTime, setIsAllTime, selectMonth } = useMonth();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<PaymentWithDetails[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [generating, setGenerating] = useState(false);
  const [blackMarketRate, setBlackMarketRate] = useState(0);
  const [totalGrossUSD, setTotalGrossUSD] = useState(0);
  const [debitGross, setDebitGross] = useState(0);
  const [creditGross, setCreditGross] = useState(0);
  const [totalPaymentsUSD, setTotalPaymentsUSD] = useState(0);
  const [paymentsCount, setPaymentsCount] = useState(0);
  const [creditCardRate, setCreditCardRate] = useState(15.42);

  const [dailyBalances, setDailyBalances] = useState<StripeDailyBalance[]>([]);
  const [payouts, setPayouts] = useState<StripePayout[]>([]);
  const [refunds, setRefunds] = useState<StripeRefund[]>([]);
  const [transactions, setTransactions] = useState<StripeTransaction[]>([]);

  const [wiseBalances, setWiseBalances] = useState<WiseBalance[]>([]);
  const [mercuryAccounts, setMercuryAccounts] = useState<MercuryAccount[]>([]);
  const [mercuryTransactions, setMercuryTransactions] = useState<MercuryTransaction[]>([]);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const realtimeActiveRef = useRef(false);
  const fallbackIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function getAllTimeMonths(): { year: number; month: number }[] {
    const start = { year: 2026, month: 1 };
    const now = new Date();
    const end = { year: now.getFullYear(), month: now.getMonth() + 1 };
    const months: { year: number; month: number }[] = [];
    let y = start.year;
    let m = start.month;
    while (y < end.year || (y === end.year && m <= end.month)) {
      months.push({ year: y, month: m });
      m++;
      if (m > 12) { m = 1; y++; }
    }
    return months;
  }

  const loadReportData = useCallback(async (silent = false) => {
    if (!selectedBusiness) return;
    try {
      if (!silent) setLoading(true);

      const today = formatDate(new Date());

      const [stripeKey, wKey, mKey] = await Promise.all([
        decryptStripeKey(selectedBusiness.stripe_key_encrypted),
        decryptWiseKey(selectedBusiness.wise_api_key_encrypted),
        decryptMercuryKey(selectedBusiness.mercury_api_key_encrypted),
      ]);

      const accountBalancesPromise = loadAccountBalances(silent, wKey, mKey);

      if (isAllTime) {
        const months = getAllTimeMonths();

        const [allPayments, allExpenses, todayRate] = await Promise.all([
          getPayments(selectedBusiness.id),
          getExpenses(selectedBusiness.id),
          getDailyRate(selectedBusiness.id, today),
          accountBalancesPromise,
        ]);

        let computedGross = 0;
        let computedFees = 0;
        let computedRefunds = 0;
        let computedNet = 0;
        let computedCount = 0;
        let computedDebitGross = 0;
        let computedCreditGross = 0;
        let allDailyBalances: StripeDailyBalance[] = [];
        let allTransactions: StripeTransaction[] = [];
        let allPayouts: StripePayout[] = [];
        let allRefunds: StripeRefund[] = [];

        if (stripeKey) {
          const monthResults = await Promise.all(
            months.map(async ({ year, month }) => {
              const ts = getMonthTimestamps(year, month);
              const [metrics, daily, txns, pouts, refs] = await Promise.all([
                fetchStripeDashboardMetrics(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => null),
                fetchStripeDailyBalances(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => []),
                fetchStripeTransactions(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => []),
                fetchStripePayouts(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => []),
                fetchStripeRefunds(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => ({ refunds: [] })),
              ]);
              return { metrics, daily, txns, pouts, refs };
            })
          );

          for (const { metrics, daily, txns, pouts, refs } of monthResults) {
            if (metrics) {
              computedGross += metrics.gross_volume / 100;
              computedFees += metrics.total_fees / 100;
              computedRefunds += metrics.total_refunds / 100;
              computedNet += metrics.net_revenue / 100;
              computedCount += metrics.charge_count;
              computedDebitGross += (metrics.debit_gross ?? metrics.gross_volume) / 100;
              computedCreditGross += (metrics.credit_gross ?? 0) / 100;
            } else {
              const txnArr = txns as StripeTransaction[];
              const charges = txnArr.filter(t => t.type === 'charge' || t.type === 'payment');
              const refundTxns = txnArr.filter(t => t.type === 'refund' || t.type === 'payment_refund');
              const chargeGross = charges.reduce((s, t) => s + t.amount, 0) / 100;
              computedGross += chargeGross;
              computedFees += charges.reduce((s, t) => s + t.fee, 0) / 100;
              computedRefunds += refundTxns.reduce((s, t) => s + Math.abs(t.amount), 0) / 100;
              computedNet += charges.reduce((s, t) => s + t.net, 0) / 100;
              computedCount += charges.length;
              computedDebitGross += chargeGross;
            }
            allDailyBalances = allDailyBalances.concat(daily as StripeDailyBalance[]);
            allTransactions = allTransactions.concat(txns as StripeTransaction[]);
            allPayouts = allPayouts.concat(pouts as StripePayout[]);
            allRefunds = allRefunds.concat((refs as { refunds: StripeRefund[] }).refunds);
          }
        }

        if (computedCount === 0 && allPayments.length > 0) {
          computedNet = allPayments.reduce((sum, p) => sum + p.amount_usd / 100, 0);
          computedCount = allPayments.length;
          computedDebitGross = computedNet;
        }

        allDailyBalances.sort((a, b) => a.date.localeCompare(b.date));
        allTransactions.sort((a, b) => b.created - a.created);
        allPayouts.sort((a, b) => b.created - a.created);

        setPayments(allPayments);
        setExpenses(allExpenses);
        setDailyBalances(allDailyBalances);
        setTransactions(allTransactions);
        setPayouts(allPayouts);
        setRefunds(allRefunds);
        setTotalGrossUSD(computedGross);
        setDebitGross(computedDebitGross);
        setCreditGross(computedCreditGross);
        setTotalPaymentsUSD(computedNet);
        setPaymentsCount(computedCount);
        setCreditCardRate(selectedBusiness.credit_card_rate);

        if (todayRate) {
          setBlackMarketRate(todayRate.black_market_rate);
        } else {
          setBlackMarketRate(selectedBusiness.default_black_market_rate);
        }
      } else {
        const { start, end } = getMonthRange(selectedMonth.year, selectedMonth.month);
        const paymentDateRange = { start, end };
        const expenseDateRange = { start: start.split('T')[0], end: end.split('T')[0] };
        const ts = getMonthTimestamps(selectedMonth.year, selectedMonth.month);

        const metricsPromise = stripeKey
          ? fetchStripeDashboardMetrics(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => null)
          : Promise.resolve(null);

        const stripeDailyPromise = stripeKey
          ? fetchStripeDailyBalances(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => [])
          : Promise.resolve([]);

        const stripePayoutsPromise = stripeKey
          ? fetchStripePayouts(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => [])
          : Promise.resolve([]);

        const stripeRefundsPromise = stripeKey
          ? fetchStripeRefunds(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => ({ refunds: [] }))
          : Promise.resolve({ refunds: [] as StripeRefund[] });

        const stripeTransactionsPromise = stripeKey
          ? fetchStripeTransactions(stripeKey, ts.startTimestamp, ts.endTimestamp).catch(() => [])
          : Promise.resolve([]);

        const [monthPayments, monthExpenses, todayRate, metrics, daily, payoutsData, refundsData, txnData] = await Promise.all([
          getPayments(selectedBusiness.id, paymentDateRange),
          getExpenses(selectedBusiness.id, expenseDateRange),
          getDailyRate(selectedBusiness.id, today),
          metricsPromise,
          stripeDailyPromise,
          stripePayoutsPromise,
          stripeRefundsPromise,
          stripeTransactionsPromise,
          accountBalancesPromise,
        ]);

        setPayments(monthPayments);
        setExpenses(monthExpenses);
        setDailyBalances(daily);
        setPayouts(payoutsData);
        setRefunds(refundsData.refunds);
        setTransactions(txnData);

        if (metrics) {
          setTotalGrossUSD(metrics.gross_volume / 100);
          setDebitGross((metrics.debit_gross ?? metrics.gross_volume) / 100);
          setCreditGross((metrics.credit_gross ?? 0) / 100);
          setTotalPaymentsUSD(metrics.net_revenue / 100);
          setPaymentsCount(metrics.charge_count);
        } else {
          const localNet = monthPayments.reduce((sum, p) => sum + p.amount_usd / 100, 0);
          setTotalGrossUSD(0);
          setDebitGross(0);
          setCreditGross(0);
          setTotalPaymentsUSD(localNet);
          setPaymentsCount(monthPayments.length);
        }

        setCreditCardRate(selectedBusiness.credit_card_rate);

        if (todayRate) {
          setBlackMarketRate(todayRate.black_market_rate);
        } else {
          setBlackMarketRate(selectedBusiness.default_black_market_rate);
        }
      }
    } catch {
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedBusiness, selectedMonth, isAllTime]);

  const loadAccountBalances = useCallback(async (silent: boolean, wKey: string | null, mKey: string | null) => {
    if (!selectedBusiness) return;
    try {
      const wisePromise = wKey
        ? fetchWiseProfiles(wKey).then(async (profiles) => {
            const businessProfile = profiles.find(p => p.type === 'BUSINESS') || profiles[0];
            if (businessProfile) {
              const balances = await fetchWiseBalances(wKey, businessProfile.id);
              setWiseBalances(balances.filter(b => b.visible !== false));
            }
          }).catch(() => {})
        : Promise.resolve((() => { if (!silent) setWiseBalances([]); })());

      const mercuryPromise = mKey
        ? fetchMercuryAccounts(mKey).then(async (response) => {
            const accounts = response.accounts || [];
            setMercuryAccounts(accounts);

            if (accounts.length > 0) {
              let mercTxnStartDate: string | undefined;
              let mercTxnEndDate: string | undefined;

              if (!isAllTime) {
                const { start, end } = getMonthRange(selectedMonth.year, selectedMonth.month);
                mercTxnStartDate = start.split('T')[0];
                mercTxnEndDate = end.split('T')[0];
              }

              const txnResults = await Promise.all(
                accounts.map(account =>
                  fetchMercuryTransactions(mKey, account.id, mercTxnStartDate, mercTxnEndDate, 500)
                    .then(r => r.transactions || [])
                    .catch(() => [] as MercuryTransaction[])
                )
              );
              setMercuryTransactions(txnResults.flat());
            }
          }).catch(() => {})
        : Promise.resolve((() => { if (!silent) { setMercuryAccounts([]); setMercuryTransactions([]); } })());

      await Promise.all([wisePromise, mercuryPromise]);
    } catch {
    }
  }, [selectedBusiness, selectedMonth, isAllTime]);

  const debouncedRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      loadReportData(true);
    }, 500);
  }, [loadReportData]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  useEffect(() => {
    if (!selectedBusiness) return;

    const channel = supabase
      .channel(`reports-realtime-${selectedBusiness.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payments', filter: `business_id=eq.${selectedBusiness.id}` }, debouncedRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'expenses', filter: `business_id=eq.${selectedBusiness.id}` }, debouncedRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'daily_rates', filter: `business_id=eq.${selectedBusiness.id}` }, debouncedRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'businesses', filter: `id=eq.${selectedBusiness.id}` }, debouncedRefresh)
      .subscribe((status) => {
        realtimeActiveRef.current = status === 'SUBSCRIBED';
      });

    fallbackIntervalRef.current = setInterval(() => {
      if (!realtimeActiveRef.current) {
        loadReportData(true);
      }
    }, 10000);

    return () => {
      supabase.removeChannel(channel);
      if (fallbackIntervalRef.current) clearInterval(fallbackIntervalRef.current);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [selectedBusiness, debouncedRefresh, loadReportData]);

  function previousMonth() {
    if (selectedMonth.month === 1) setSelectedMonth({ month: 12, year: selectedMonth.year - 1 });
    else setSelectedMonth({ ...selectedMonth, month: selectedMonth.month - 1 });
  }

  function nextMonth() {
    if (selectedMonth.month === 12) setSelectedMonth({ month: 1, year: selectedMonth.year + 1 });
    else setSelectedMonth({ ...selectedMonth, month: selectedMonth.month + 1 });
  }

  function isCurrentMonth() {
    if (isAllTime) return false;
    const now = new Date();
    return selectedMonth.year === now.getFullYear() && selectedMonth.month === now.getMonth() + 1;
  }

  const totalExpensesMVR = expenses.reduce((sum, e) => sum + (e.amount_mvr / 100), 0);
  const bankRate = selectedBusiness?.bank_rate || 15.42;
  const exchangeRevenueMVR = calcExchangeRevenue(totalPaymentsUSD, blackMarketRate);
  const bankCostMVR = calcBankCostSplit(debitGross, creditGross, bankRate, creditCardRate);
  const exchangeProfitMVR = calcExchangeProfitSplit(totalPaymentsUSD, blackMarketRate, debitGross, creditGross, bankRate, creditCardRate);
  const netProfitMVR = calcNetProfit(exchangeProfitMVR, totalExpensesMVR);
  const profitMargin = exchangeRevenueMVR > 0 ? (netProfitMVR / exchangeRevenueMVR) * 100 : 0;
  const avgPaymentUSD = paymentsCount > 0 ? totalPaymentsUSD / paymentsCount : 0;

  const totalFeesUSD = dailyBalances.reduce((s, d) => s + d.fees, 0) / 100;
  const totalRefundsUSD = refunds.reduce((s, r) => s + r.amount, 0) / 100;
  const totalStripeTransactions = dailyBalances.reduce((s, d) => s + d.count, 0);
  const daysInPeriod = isAllTime
    ? Math.ceil((Date.now() - Date.UTC(2020, 0, 1)) / (1000 * 60 * 60 * 24))
    : new Date(selectedMonth.year, selectedMonth.month, 0).getDate();
  const activeDays = dailyBalances.filter(d => d.count > 0).length;

  const expensesByCategory = useMemo(() => {
    return expenses.reduce((acc, e) => {
      acc[e.category] = (acc[e.category] || 0) + (e.amount_mvr / 100);
      return acc;
    }, {} as Record<string, number>);
  }, [expenses]);

  const dailyPayments = useMemo(() => {
    const days: Record<string, number> = {};
    payments.forEach(p => {
      const day = new Date(p.payment_date).getDate().toString();
      days[day] = (days[day] || 0) + (p.amount_usd / 100);
    });
    const result: { label: string; value: number }[] = [];
    if (isAllTime) {
      const monthMap: Record<string, number> = {};
      payments.forEach(p => {
        const d = new Date(p.payment_date);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        monthMap[key] = (monthMap[key] || 0) + (p.amount_usd / 100);
      });
      Object.keys(monthMap).sort().forEach(key => {
        result.push({ label: key, value: monthMap[key] });
      });
    } else {
      const daysInMonth = new Date(selectedMonth.year, selectedMonth.month, 0).getDate();
      for (let d = 1; d <= daysInMonth; d++) {
        if (days[d.toString()]) {
          result.push({ label: d.toString(), value: days[d.toString()] });
        }
      }
    }
    return result;
  }, [payments, selectedMonth, isAllTime]);

  const topCustomers = useMemo(() => {
    const customerTotals: Record<string, { name: string; total: number }> = {};
    payments.forEach(p => {
      const key = p.customer_id;
      if (!customerTotals[key]) customerTotals[key] = { name: p.customer.name, total: 0 };
      customerTotals[key].total += p.amount_usd / 100;
    });
    return Object.values(customerTotals)
      .sort((a, b) => b.total - a.total)
      .slice(0, 8)
      .map(c => ({ label: c.name, value: c.total }));
  }, [payments]);

  const paymentsByCard = useMemo(() => {
    const cardTotals: Record<string, number> = {};
    payments.forEach(p => {
      const type = p.card?.card_type || 'Unlinked';
      cardTotals[type] = (cardTotals[type] || 0) + (p.amount_usd / 100);
    });
    return Object.entries(cardTotals).map(([label, value]) => ({ label, value }));
  }, [payments]);

  const waterfallData = useMemo(() => [
    { label: 'Exchange Revenue', value: exchangeRevenueMVR, type: 'add' as const },
    { label: 'Bank Cost', value: bankCostMVR, type: 'subtract' as const },
    { label: 'Expenses', value: totalExpensesMVR, type: 'subtract' as const },
    { label: 'Net Profit', value: Math.abs(netProfitMVR), type: 'total' as const },
  ], [exchangeRevenueMVR, bankCostMVR, totalExpensesMVR, netProfitMVR]);

  async function handleGeneratePDF() {
    if (!selectedBusiness) return;
    setGenerating(true);
    try {
      await generatePDFReport({
        businessName: selectedBusiness.name,
        month: isAllTime ? 'All Time' : getMonthName(selectedMonth.month),
        year: isAllTime ? new Date().getFullYear() : selectedMonth.year,
        totalPayments: paymentsCount,
        totalPaymentsUSD,
        totalExpensesMVR,
        exchangeRevenueMVR,
        bankCostMVR,
        exchangeProfitMVR,
        netProfitMVR,
        bankRate,
        blackMarketRate,
        expensesByCategory,
        dailyPayments,
        topCustomers,
        payments: payments.map(p => ({
          date: new Date(p.payment_date).toLocaleDateString(),
          customer: p.customer.name,
          amount: p.amount_usd / 100,
          card: p.card?.card_type || 'N/A',
        })),
        expenses: expenses.map(e => ({
          date: e.expense_date,
          category: e.category,
          amount: e.amount_mvr / 100,
          note: e.note,
        })),
      });
    } catch {
    } finally {
      setGenerating(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LogoSpinner size={48} />
      </div>
    );
  }

  const hasData = paymentsCount > 0 || payments.length > 0 || expenses.length > 0;
  const hasStripeData = dailyBalances.length > 0;
  const hasAccountData = wiseBalances.length > 0 || mercuryAccounts.length > 0;

  return (
    <div className="h-full flex flex-col">
      <div className="glass-header shrink-0 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between mb-3 gap-3">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-xl bg-teal-500/10">
              <BarChart3 size={18} className="text-teal-400" />
            </div>
            <h1 className="text-lg font-bold text-white">Reports</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleGeneratePDF}
              disabled={generating || !hasData}
              className="flex items-center gap-1.5 px-3 sm:px-4 py-2 glass-button-active disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-xs font-medium"
            >
              <FileText size={14} />
              <span className="hidden sm:inline">{generating ? 'Generating...' : 'Download PDF'}</span>
              <span className="sm:hidden">{generating ? '...' : 'PDF'}</span>
            </button>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={previousMonth}
            className={`p-2 rounded-xl glass-button ${isAllTime ? 'opacity-40 pointer-events-none' : ''}`}
          >
            <ChevronLeft size={16} className="text-teal-400" />
          </button>
          <div className={`text-center min-w-[140px] ${isAllTime ? 'opacity-50' : ''}`}>
            <h2 className="text-base font-bold text-white">{getMonthName(selectedMonth.month)}</h2>
            <p className="text-xs text-white">{selectedMonth.year}</p>
          </div>
          <button
            onClick={nextMonth}
            className={`p-2 rounded-xl glass-button ${isAllTime ? 'opacity-40 pointer-events-none' : ''}`}
          >
            <ChevronRight size={16} className="text-teal-400" />
          </button>
          <button
            onClick={() => setIsAllTime(!isAllTime)}
            className={`px-3 py-2 text-xs font-semibold rounded-xl ${
              isAllTime
                ? 'glass-button-active text-white'
                : 'glass-button text-white'
            }`}
          >
            All Time
          </button>
          {!isCurrentMonth() && !isAllTime && (
            <button
              onClick={() => {
                const now = new Date();
                selectMonth({ year: now.getFullYear(), month: now.getMonth() + 1 });
              }}
              className="hidden sm:block px-3 py-2 text-white text-xs font-semibold rounded-xl glass-button-active"
            >
              Current Month
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {!hasData && !hasStripeData && !hasAccountData ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <BarChart3 size={48} className="text-white mb-3" />
            <p className="text-white text-sm font-medium">No data for this period</p>
            <p className="text-white text-xs mt-1">Payments and expenses will appear here once recorded</p>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto space-y-5">
            <KPIRow
              totalPayments={paymentsCount}
              totalPaymentsUSD={totalPaymentsUSD}
              exchangeProfitMVR={exchangeProfitMVR}
              netProfitMVR={netProfitMVR}
              profitMargin={profitMargin}
              avgPaymentUSD={avgPaymentUSD}
            />

            <ExplanationBanner
              netProfitMVR={netProfitMVR}
              exchangeProfitMVR={exchangeProfitMVR}
              totalExpensesMVR={totalExpensesMVR}
              totalPaymentsUSD={totalPaymentsUSD}
              bankRate={bankRate}
              blackMarketRate={blackMarketRate}
              paymentCount={paymentsCount}
              isAllTime={isAllTime}
            />

            {hasAccountData && (
              <AccountsReportSection
                wiseBalances={wiseBalances}
                mercuryAccounts={mercuryAccounts}
                mercuryTransactions={mercuryTransactions}
                isAllTime={isAllTime}
                monthLabel={`${getMonthName(selectedMonth.month)} ${selectedMonth.year}`}
              />
            )}

            {hasStripeData && (
              <PerformanceGaugesSection
                totalGrossUSD={totalGrossUSD}
                totalFeesUSD={totalFeesUSD}
                totalRefundsUSD={totalRefundsUSD}
                profitMargin={profitMargin}
                transactionCount={totalStripeTransactions}
                activeDays={activeDays}
                daysInMonth={daysInPeriod}
              />
            )}

            <WaterfallSection data={waterfallData} />

            {hasStripeData && <RevenueVsFeesSection dailyBalances={dailyBalances} />}

            {hasStripeData && <CumulativeRevenueSection dailyBalances={dailyBalances} />}

            {hasStripeData && <DailyRevenueStackedSection dailyBalances={dailyBalances} />}

            {dailyPayments.length > 0 && <DailyPaymentsSection data={dailyPayments} isAllTime={isAllTime} />}

            {hasStripeData && !isAllTime && (
              <TransactionHeatmapSection
                dailyBalances={dailyBalances}
                year={selectedMonth.year}
                month={selectedMonth.month}
              />
            )}

            <div className="grid grid-cols-1 fold:grid-cols-2 gap-5">
              {topCustomers.length > 0 && <TopCustomersSection data={topCustomers} />}
              {paymentsByCard.length > 0 && <CardTypeSection data={paymentsByCard} />}
            </div>

            <div className="grid grid-cols-1 fold:grid-cols-2 gap-5">
              {transactions.length > 0 && <TransactionTypeSection transactions={transactions} />}
              {payouts.length > 0 && <PayoutTimelineSection payouts={payouts} />}
            </div>

            {refunds.length > 0 && (
              <RefundSummarySection refunds={refunds} totalGrossUSD={totalGrossUSD} />
            )}

            <ExchangeBreakdownSection
              totalPaymentsUSD={totalPaymentsUSD}
              totalGrossUSD={totalGrossUSD}
              debitGross={debitGross}
              creditGross={creditGross}
              bankRate={bankRate}
              creditCardRate={creditCardRate}
              blackMarketRate={blackMarketRate}
              exchangeRevenueMVR={exchangeRevenueMVR}
              bankCostMVR={bankCostMVR}
              exchangeProfitMVR={exchangeProfitMVR}
              totalExpensesMVR={totalExpensesMVR}
              netProfitMVR={netProfitMVR}
            />

            {Object.keys(expensesByCategory).length > 0 && (
              <ExpenseBreakdownSection
                expensesByCategory={expensesByCategory}
                totalExpensesMVR={totalExpensesMVR}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function KPIRow({ totalPayments, totalPaymentsUSD, exchangeProfitMVR, netProfitMVR, profitMargin, avgPaymentUSD }: {
  totalPayments: number; totalPaymentsUSD: number; exchangeProfitMVR: number; netProfitMVR: number; profitMargin: number; avgPaymentUSD: number;
}) {
  const kpis = [
    { label: 'Payments', value: totalPayments.toString(), sub: 'Total transactions', icon: Receipt, iconBg: 'bg-blue-500/10', iconColor: 'text-blue-500' },
    { label: 'Revenue', value: formatCurrency(totalPaymentsUSD), sub: 'Total USD collected', icon: DollarSign, iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400' },
    { label: 'Avg Payment', value: formatCurrency(avgPaymentUSD), sub: 'Per transaction', icon: Calendar, iconBg: 'bg-sky-500/10', iconColor: 'text-sky-500' },
    { label: 'Exchange Profit', value: formatCurrency(exchangeProfitMVR, 'MVR'), sub: 'From currency spread', icon: Wallet, iconBg: 'bg-teal-500/10', iconColor: 'text-teal-500' },
    { label: 'Net Profit', value: formatCurrency(netProfitMVR, 'MVR'), sub: 'After all expenses', icon: netProfitMVR >= 0 ? TrendingUp : TrendingDown, iconBg: netProfitMVR >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10', iconColor: netProfitMVR >= 0 ? 'text-emerald-400' : 'text-red-500', valueColor: netProfitMVR >= 0 ? 'text-emerald-800' : 'text-red-600' },
    { label: 'Margin', value: `${profitMargin.toFixed(1)}%`, sub: 'Net profit margin', icon: BarChart3, iconBg: profitMargin >= 0 ? 'bg-teal-500/10' : 'bg-red-500/10', iconColor: profitMargin >= 0 ? 'text-teal-500' : 'text-red-500' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
      {kpis.map((k) => (
        <div key={k.label} className="glass-panel rounded-2xl p-3 sm:p-4 transition-all duration-200 hover:shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-medium text-white">{k.label}</span>
            <div className={`p-1 rounded-lg ${k.iconBg}`}>
              <k.icon size={13} className={k.iconColor} />
            </div>
          </div>
          <div className={`text-base sm:text-lg font-bold break-all sm:break-normal ${(k as any).valueColor || 'text-white'}`}>{k.value}</div>
          <div className="text-[10px] text-white mt-0.5">{k.sub}</div>
        </div>
      ))}
    </div>
  );
}

function ExplanationBanner({ netProfitMVR, exchangeProfitMVR, totalExpensesMVR, totalPaymentsUSD, bankRate, blackMarketRate, paymentCount, isAllTime }: {
  netProfitMVR: number; exchangeProfitMVR: number; totalExpensesMVR: number; totalPaymentsUSD: number; bankRate: number; blackMarketRate: number; paymentCount: number; isAllTime: boolean;
}) {
  const spread = blackMarketRate - bankRate;
  const isProfit = netProfitMVR >= 0;
  const periodText = isAllTime ? 'Across all time' : 'This month';

  return (
    <div className={`rounded-2xl border p-5 backdrop-blur-xl ${isProfit ? 'bg-emerald-500/10 border-emerald-200/60' : 'bg-red-500/10 border-red-200/60'}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-xl ${isProfit ? 'bg-emerald-500/15' : 'bg-red-500/15'} mt-0.5`}>
          <Info size={16} className={isProfit ? 'text-emerald-800' : 'text-red-600'} />
        </div>
        <div className="flex-1">
          <h3 className={`text-sm font-bold ${isProfit ? 'text-emerald-800' : 'text-red-800'} mb-1.5`}>
            {isAllTime ? 'All-Time Summary' : 'Monthly Summary'}
          </h3>
          <p className="text-xs leading-relaxed text-white">
            {periodText}, <strong>{paymentCount} payment{paymentCount !== 1 ? 's' : ''}</strong> were processed
            totaling <strong>{formatCurrency(totalPaymentsUSD)}</strong>.
            The exchange rate spread of <strong>{spread.toFixed(2)} MVR</strong> per dollar
            (selling at {blackMarketRate} MVR, buying at {bankRate} MVR)
            generated an exchange profit of <strong>{formatCurrency(exchangeProfitMVR, 'MVR')}</strong>.
            {totalExpensesMVR > 0 && (
              <> After deducting <strong>{formatCurrency(totalExpensesMVR, 'MVR')}</strong> in expenses, </>
            )}
            {isProfit ? (
              <> the business earned a net profit of <strong className="text-emerald-400">{formatCurrency(netProfitMVR, 'MVR')}</strong>.</>
            ) : (
              <> the business recorded a net loss of <strong className="text-red-700">{formatCurrency(Math.abs(netProfitMVR), 'MVR')}</strong>.
              {totalExpensesMVR > exchangeProfitMVR && ' Expenses exceeded the exchange profit this period.'}</>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

function WaterfallSection({ data }: { data: { label: string; value: number; type: 'add' | 'subtract' | 'total' }[] }) {
  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="p-1.5 rounded-xl bg-teal-500/10">
          <TrendingUp size={16} className="text-teal-400" />
        </div>
        <h2 className="text-sm font-bold text-white">Profit Flow</h2>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        Shows how revenue flows through costs to arrive at net profit. Green bars add value, red bars represent deductions.
      </p>
      <WaterfallChart items={data} />
      <div className="flex items-center gap-4 mt-3 ml-2 text-[10px] text-white">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500 inline-block" /> Income</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-500 inline-block" /> Deduction</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-800 inline-block" /> Net Result</span>
      </div>
    </div>
  );
}

function DailyPaymentsSection({ data, isAllTime }: { data: { label: string; value: number }[]; isAllTime: boolean }) {
  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="p-1.5 rounded-xl bg-sky-500/10">
          <Calendar size={16} className="text-sky-600" />
        </div>
        <h2 className="text-sm font-bold text-white">
          {isAllTime ? 'Monthly Payment Volume' : 'Daily Payment Volume'}
        </h2>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        {isAllTime
          ? 'Payment amounts by month across all time.'
          : 'Payment amounts by day. Shows which days had the highest transaction volumes.'}
      </p>
      <MiniLineChart data={data} color="#0d9488" currency="$" />
    </div>
  );
}

function TopCustomersSection({ data }: { data: { label: string; value: number }[] }) {
  return (
    <div className="glass-panel rounded-2xl p-5 overflow-hidden">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="p-1.5 rounded-xl bg-blue-500/10">
          <Users size={16} className="text-blue-600" />
        </div>
        <h2 className="text-sm font-bold text-white">Top Customers</h2>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        Customers ranked by total payment volume this period.
      </p>
      <BarChart data={data} currency="$" />
    </div>
  );
}

function CardTypeSection({ data }: { data: { label: string; value: number }[] }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="p-1.5 rounded-xl bg-amber-500/10">
          <Wallet size={16} className="text-amber-600" />
        </div>
        <h2 className="text-sm font-bold text-white">Payment by Card Type</h2>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        Distribution of payments across different card types.
      </p>
      <DonutChart data={data} centerLabel="Total" centerValue={`$${total.toFixed(2)}`} />
    </div>
  );
}

function ExchangeBreakdownSection({ totalPaymentsUSD, debitGross, creditGross, bankRate, creditCardRate, blackMarketRate, exchangeRevenueMVR, bankCostMVR, exchangeProfitMVR, totalExpensesMVR, netProfitMVR }: {
  totalPaymentsUSD: number; totalGrossUSD: number; debitGross: number; creditGross: number; bankRate: number; creditCardRate: number; blackMarketRate: number; exchangeRevenueMVR: number; bankCostMVR: number; exchangeProfitMVR: number; totalExpensesMVR: number; netProfitMVR: number;
}) {
  const bankCostDesc = creditGross > 0
    ? `Debit $${debitGross.toFixed(0)}x${bankRate.toFixed(2)} + Credit $${creditGross.toFixed(0)}x${creditCardRate.toFixed(2)}`
    : `$${debitGross.toFixed(2)} x ${bankRate} MVR`;

  const rows = [
    { label: `Revenue (${totalPaymentsUSD.toFixed(2)} USD x ${blackMarketRate} MVR)`, value: exchangeRevenueMVR, color: 'text-white', bg: 'glass-inner', icon: <ArrowRight size={12} className="text-emerald-400" /> },
    { label: `Bank Cost (${bankCostDesc})`, value: -bankCostMVR, color: 'text-red-500', bg: 'bg-red-500/10', icon: <ArrowRight size={12} className="text-red-400" /> },
    { label: 'Exchange Profit', value: exchangeProfitMVR, color: 'text-teal-400', bg: 'bg-teal-500/10', bold: true, border: 'border-teal-200' },
    { label: 'Total Expenses', value: -totalExpensesMVR, color: 'text-red-500', bg: 'bg-red-500/10', icon: <ArrowRight size={12} className="text-red-400" /> },
    { label: 'Net Profit', value: netProfitMVR, color: netProfitMVR >= 0 ? 'text-emerald-800' : 'text-red-600', bg: netProfitMVR >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10', bold: true, border: netProfitMVR >= 0 ? 'border-emerald-300' : 'border-red-300' },
  ];

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="p-1.5 rounded-xl bg-teal-500/10">
          <BarChart3 size={16} className="text-teal-400" />
        </div>
        <h2 className="text-sm font-bold text-white">Detailed Profit Calculation</h2>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        Step-by-step breakdown of how net profit is calculated from the exchange rate spread.
      </p>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.label} className={`flex items-center justify-between py-2.5 px-4 rounded-xl ${r.bg} ${(r as any).border ? `border ${(r as any).border}` : ''}`}>
            <div className="flex items-center gap-2">
              {(r as any).icon && (r as any).icon}
              <span className={`text-xs ${(r as any).bold ? 'font-bold text-white' : 'text-white'}`}>{r.label}</span>
            </div>
            <span className={`text-xs font-bold ${r.color}`}>
              {r.value < 0 ? '-' : ''}{formatCurrency(Math.abs(r.value), 'MVR')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExpenseBreakdownSection({ expensesByCategory, totalExpensesMVR }: { expensesByCategory: Record<string, number>; totalExpensesMVR: number }) {
  const catEntries = Object.entries(expensesByCategory).sort(([, a], [, b]) => b - a);
  const donutData = catEntries.map(([label, value]) => ({ label, value }));

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-xl bg-amber-500/10">
            <Receipt size={16} className="text-amber-600" />
          </div>
          <h2 className="text-sm font-bold text-white">Expense Breakdown</h2>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-white">Total Expenses</div>
          <div className="text-base font-bold text-amber-600">{formatCurrency(totalExpensesMVR, 'MVR')}</div>
        </div>
      </div>
      <p className="text-[11px] text-white mb-4 ml-9">
        How expenses are distributed across categories. Larger slices represent higher spending areas.
      </p>
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-shrink-0">
          <DonutChart data={donutData} centerLabel="Total" centerValue={formatCurrency(totalExpensesMVR, 'MVR')} size={180} />
        </div>
        <div className="flex-1 space-y-3">
          {catEntries.map(([category, amount]) => {
            const percentage = totalExpensesMVR > 0 ? (amount / totalExpensesMVR) * 100 : 0;
            return (
              <div key={category} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-white">{category}</span>
                  <div className="text-right">
                    <span className="text-xs font-semibold text-white">{formatCurrency(amount, 'MVR')}</span>
                    <span className="text-[10px] text-white ml-1.5">({percentage.toFixed(1)}%)</span>
                  </div>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                  <div className="bg-teal-500 h-2 rounded-full transition-all duration-500" style={{ width: `${percentage}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
